import { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Upload, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * FileUpload: value = current URL (relative or absolute), onChange(newUrl).
 * kind = 'video' | 'thumbnail' | 'poster'
 * accept = accepted MIME/extension list
 */
export default function FileUpload({ label, value, onChange, kind = "misc", accept, testId }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);

    const pick = () => inputRef.current?.click();

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        setProgress(0);
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post(`/admin/upload?kind=${kind}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            onChange(data.url); // relative /api/files/... path
            toast.success(`${kind} uploaded`);
        } catch (err) {
            const msg = err?.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Upload failed");
        } finally {
            setBusy(false);
            setProgress(0);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const clear = () => onChange("");

    // Absolute URL for preview
    const previewSrc = value
        ? value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`
        : "";

    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
            <div className="mt-1 flex items-center gap-3">
                {/* Preview */}
                <div className="w-16 h-16 rounded-[10px] bg-white/5 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {value ? (
                        kind === "video" ? (
                            <video src={previewSrc} className="w-full h-full object-cover" />
                        ) : (
                            <img src={previewSrc} alt="" className="w-full h-full object-cover" />
                        )
                    ) : (
                        <Upload className="w-5 h-5 text-white/30" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <input
                        data-testid={testId}
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="Paste URL or upload"
                        className="w-full bg-white/5 border border-white/10 rounded-[10px] px-3 py-2 text-white text-sm focus:border-[#FFD400] outline-none transition-colors"
                    />
                    <div className="mt-2 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={pick}
                            disabled={busy}
                            data-testid={`${testId}-upload-btn`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#FFD400] text-black rounded-full px-3 py-1.5 hover:scale-105 transition-transform disabled:opacity-60 disabled:hover:scale-100"
                        >
                            {busy ? (
                                <>
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {progress}%
                                </>
                            ) : (
                                <>
                                    <Upload className="w-3.5 h-3.5" /> Upload
                                </>
                            )}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={clear}
                                className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-red-400 transition-colors"
                            >
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        )}
                        {value && !busy && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                                <Check className="w-3 h-3" /> Set
                            </span>
                        )}
                    </div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={(e) => upload(e.target.files?.[0])}
                    />
                </div>
            </div>
        </div>
    );
}
